import React, { FunctionComponent, useEffect, useState } from 'react';
import {
    Col,
    Dropdown,
    DropdownButton,
    Row,
} from 'react-bootstrap';
import { ApplicationState } from '../../../redux/States';
import { ErrorDispatches } from '../../../redux/Dispatches';
import { ConnectedProps, connect } from 'react-redux';
import {
    AuthedLayout,
    EditableTableDiv,
    HeaderDisplayKeysWithType,
} from '../../../components';
import { useClassGrade, useSemGrade } from '../../../hook';
import { Sight, StudentGradeNoSeat, UserInfo, WH } from '../../../model';
import apis from '../../../apis';
import { SightContext } from '../../TableHeaders/SightHeader';
import {
    downloadDataAsExcel,
    printDataAsTable,
    SheetHeaderDisplay,
} from '../../../utils';
import { whbmiDiag } from '../../../utils/sight';
import { Nullable } from '../../../types';
import { DateTime } from 'luxon';
import { toast } from 'react-toastify';
type PageData = StudentGradeNoSeat & Nullable<Sight> & Nullable<WH> & { diag: string };
const formatSightValue = (value: unknown) => {
    if (typeof value !== 'number') return value as string;

    if (value === -1) return '<0.1';
    if (value < -1) return String(value);

    return Number.isInteger(value) ? value.toFixed(1) : String(value);
};
const tableHeader: (SheetHeaderDisplay<PageData> &
    HeaderDisplayKeysWithType<PageData>)[] = [
        { display: '座號', property: 'seat' },
        { display: '姓名', property: 'name' },
        {
            display: '性別',
            property: 'sex',
            onRender: (v) => (v === '1' ? '男' : '女'),
            onSheetRender: (v) => (v === '1' ? '男' : '女'),
        },
        { display: '身高', property: 'height' },
        { display: '體重', property: 'weight' },
        {
            display: '測量日',
            property: 'examDate',
            onSheetRender: (v) => {
                return v instanceof DateTime ? v.toFormat('yyyy/MM/dd') : ('' as string);
            },
            onRender: (v) => {
                return v instanceof DateTime ? v.toFormat('yyyy/MM/dd') : v;
            },
        },
        {
            display: 'BMI',
            property: 'bmi',
            onRender: (value: unknown) => {
                if (typeof value === 'number') {
                    return value.toFixed(1); // Ensure one decimal place
                }
                return value;
            },
            onSheetRender: (value: unknown) => {
                if (typeof value === 'number') {
                    return value.toFixed(1); // Ensure one decimal place
                }
                return value as string;
            },
        },
        { display: '體位', property: 'diag' },
        {
            display: '裸視右',
            property: 'sight0R',
            onRender: (value) => formatSightValue(value),
            onSheetRender: (value) => formatSightValue(value),
        },
        {
            display: '裸視左',
            property: 'sight0L',
            onRender: (value) => formatSightValue(value),
            onSheetRender: (value) => formatSightValue(value),
        },
        {
            display: '戴鏡右',
            property: 'sightR',
            onRender: (value) => formatSightValue(value),
            onSheetRender: (value) => formatSightValue(value),
        },
        {
            display: '戴鏡左',
            property: 'sightL',
            onRender: (value) => formatSightValue(value),
            onSheetRender: (value) => formatSightValue(value),
        }
    ];

const mapState = (app: ApplicationState) => ({ ...app.auth });
const mapDispatch = ErrorDispatches;

const connector = connect(mapState, mapDispatch);
type Props = ConnectedProps<typeof connector>;

const whClassSight: FunctionComponent<Props> = ({
    user,
    catchErrorForModal,
}) => {
    const { yearSem, element: semGradeElement } = useSemGrade();
    const { selectedClassGrade: classGrade, element: classGradeElement } =
        useClassGrade({
            yearSem
        });
    const [students, setStudents] = useState<PageData[]>([]);
    const [userinfo, setUser] = useState<UserInfo>();
    useEffect(() => {
        if (yearSem && classGrade) {
            apis.getUserInfo()
                .then((u) => {
                    setUser(u);
                })
                .catch(catchErrorForModal);
            toast
                .promise(
                    apis
                        .getWHClassSight(yearSem.year, yearSem.sem, classGrade.id)
                        .then(r => {
                            setStudents(
                                r.map(({ sight, wh, ...s }) => ({
                                    ...sight,
                                    ...wh,
                                    ...s,
                                    diag: whbmiDiag(wh?.bmiCode?.toString()),
                                }))
                            )
                        }),
                    {
                        pending: '資料載入中......',
                        success: '資料載入成功！',
                        error: '資料載入失敗！請查看錯誤資訊。',
                    }
                )
                .catch(catchErrorForModal);
        }
    }, [classGrade?.id, yearSem?.year, yearSem?.sem]);

    return (
        <AuthedLayout>
            <Row className="justify-content-between">
                <Col>
                    <Row>
                        <Col xs={3} className="mr-3">
                            {semGradeElement}
                        </Col>
                        <Col xs={3} className="mr-3">
                            {classGradeElement}
                        </Col>
                    </Row>
                </Col>
                <div className="d-flex">
                    {/* 所有班級 */}
                    <DropdownButton
                        id="all-wh-class-sight-export"
                        title="所有班級"
                        variant="info"
                        className="mr-3"
                        disabled={!yearSem}
                    >
                        <Dropdown.Item
                            onClick={() => {
                                if (!yearSem) return;

                                apis
                                    .getWHClassSight(
                                        yearSem.year,
                                        yearSem.sem
                                    )
                                    .then((r) => {
                                        const allStudents = r.map(
                                            ({ sight, wh, ...s }) => ({
                                                ...(sight ?? {}),
                                                ...(wh ?? {}),
                                                ...s,
                                                diag: whbmiDiag(
                                                    wh?.bmiCode?.toString()
                                                ),
                                            })
                                        );

                                        downloadDataAsExcel({
                                            title:
                                                `${user.schools[user.currentSchool].name}` +
                                                `${yearSem.year}學年` +
                                                `${yearSem.sem}學期_班級身高體重視力清單`,
                                            values: allStudents,
                                            headers: tableHeader,
                                            context: SightContext,
                                            groupBy: (v) =>
                                                `${v.grade}年${v.className}班`,
                                            footer:
                                                '承辦人:　　　　　　　　　　組長:　　　　　　　　　　主任:　　　　　　　　　　　校長:　　　　　　　　　',
                                            exportfile:
                                                userinfo?.exportFileType,
                                        });
                                    })
                                    .catch(catchErrorForModal);
                            }}
                        >
                            Excel 下載
                        </Dropdown.Item>

                        <Dropdown.Item
                            onClick={() => {
                                if (!yearSem) return;

                                apis
                                    .getWHClassSight(
                                        yearSem.year,
                                        yearSem.sem
                                    )
                                    .then((r) => {
                                        const allStudents = r.map(
                                            ({ sight, wh, ...s }) => ({
                                                ...(sight ?? {}),
                                                ...(wh ?? {}),
                                                ...s,
                                                diag: whbmiDiag(
                                                    wh?.bmiCode?.toString()
                                                ),
                                            })
                                        );

                                        printDataAsTable({
                                            title:
                                                `${user.schools[user.currentSchool].name}` +
                                                `${yearSem.year}學年` +
                                                `${yearSem.sem}學期_班級身高體重視力清單`,
                                            values: allStudents,
                                            headers: tableHeader,
                                            context: SightContext,
                                            groupBy: (v) =>
                                                `${v.grade}年${v.className}班`,
                                            footer:
                                                '承辦人:　　　　　　　　　　　　　組長:　　　　　　　　　　　　　主任:　　　　　　　　　　　　　　校長:　　　　　　　　　　　　',
                                        });
                                    })
                                    .catch(catchErrorForModal);
                            }}
                        >
                            列印
                        </Dropdown.Item>
                    </DropdownButton>

                    {/* 單一班級 */}
                    <DropdownButton
                        id="single-wh-class-sight-export"
                        title="單一班級"
                        variant="success"
                        disabled={!students.length}
                    >
                        <Dropdown.Item
                            onClick={() => {
                                downloadDataAsExcel({
                                    title:
                                        `${user.schools[user.currentSchool].name}` +
                                        `${yearSem?.year}學年` +
                                        `${yearSem?.sem}學期_班級身高體重視力清單`,
                                    values: students,
                                    headers: tableHeader,
                                    context: SightContext,
                                    groupBy: () =>
                                        `${classGrade?.name ?? ''}`,
                                    footer:
                                        '承辦人:　　　　　　　　　　　　　組長:　　　　　　　　　　　　　主任:　　　　　　　　　　　　　　校長:　　　　　　　　　　　　',
                                    exportfile:
                                        userinfo?.exportFileType,
                                });
                            }}
                        >
                            Excel 下載
                        </Dropdown.Item>

                        <Dropdown.Item
                            onClick={() => {
                                printDataAsTable({
                                    title:
                                        `${user.schools[user.currentSchool].name}` +
                                        `${yearSem?.year}學年` +
                                        `${yearSem?.sem}學期_班級身高體重視力清單`,
                                    values: students,
                                    headers: tableHeader,
                                    context: SightContext,
                                    groupBy: () =>
                                        `${classGrade?.name ?? ''}`,
                                    footer:
                                        '承辦人:　　　　　　　　　　　　　組長:　　　　　　　　　　　　　主任:　　　　　　　　　　　　　　校長:　　　　　　　　　　　　',
                                });
                            }}
                        >
                            列印
                        </Dropdown.Item>
                    </DropdownButton>
                </div>
            </Row>
            <hr />
            <Row>
                <EditableTableDiv
                    headers={tableHeader}
                    values={students}
                    context={SightContext}
                />
            </Row>
        </AuthedLayout>
    );
};

export const WHClassSight = connector(whClassSight);